/**
 * Las paletas que puede elegir una clínica.
 *
 * Antes había dos selectores de color libres y eso terminaba, tarde o
 * temprano, en un panel amarillo flúor sobre blanco: nadie que administra una
 * clínica tiene por qué saber qué pasa con el contraste cuando elige el color
 * que le gusta. Acá se elige una combinación ya armada.
 *
 * Cada paleta es un color vivo y uno pastel, y ese reparto no es estético sino
 * funcional: el vivo pinta lo que tiene que resaltar sobre el fondo —botones,
 * enlaces, el foco del teclado— y el pastel pinta superficies grandes que
 * llevan texto encima —la fila seleccionada, una etiqueta, el círculo de una
 * inicial—. Dos colores vivos juntos pelean; dos pasteles no se ven.
 */

/**
 * La marca del producto, fija.
 *
 * Dejó de ser elegible por clínica: una marca de producto no es una preferencia
 * de cada instalación. Los valores viven acá solos, así que cambiarlos es
 * cambiar una constante.
 *
 * El reparto de papeles no es el orden en que se nombran los colores, sino el
 * que permite leer la pantalla. Medido:
 *
 *  - **Lavanda** `#E6E6FA` tiene una luminancia de 0,80: es un pastel. Como
 *    superficie con texto oscuro encima da 14,5:1, de sobra; como color de un
 *    botón con texto blanco daría 1,2:1, que es texto invisible. Va de acento.
 *  - **Tomate** `#FF6347` con texto blanco da 2,9:1 y como texto sobre blanco
 *    2,95:1 — las dos por debajo del mínimo legible. En su forma plena sirve
 *    para rellenar formas: barras, series, marcas.
 *  - Para lo que lleva texto o *es* texto —botones, enlaces, foco— se usa el
 *    mismo tomate profundizado hasta 4,8:1 con blanco. Es el mismo color de la
 *    marca un paso más oscuro, no otro color.
 */
export const BRAND = {
  /** Salvia gris: superficies seleccionadas y fondos secundarios. */
  accent: "#D9DFDA",
  /** Alias compatible para el petróleo usado en rellenos y marcas. */
  tomato: "#26464A",
  /** Niebla: fondo principal del producto. */
  surface: "#F1F3F0",
  /** Petróleo principal: botones, enlaces y foco del teclado. */
  primary: "#26464A",
} as const;

export interface Palette {
  id: string;
  name: string;
  /** Vivo: sobre él va texto claro. */
  primary: string;
  /** Pastel: sobre él va texto oscuro. */
  accent: string;
}

/**
 * El catálogo de paletas.
 *
 * Ya no se ofrece en pantalla —la marca la fija `BRAND`— pero se conserva: es
 * el barrido con el que las pruebas del tema comprueban que la escala de
 * gráficos y el acento se comportan con dieciséis tonos distintos, incluidos
 * los que rompieron cosas. Si mañana la marca cambia a otro violeta, esas
 * pruebas siguen cubriendo el caso.
 */
export const PALETTES: Palette[] = [
  { id: "teal", name: "Teal clínico", primary: "#0F766E", accent: "#CCFBF1" },
  { id: "azul", name: "Azul profundo", primary: "#1D4ED8", accent: "#DBEAFE" },
  { id: "violeta", name: "Violeta", primary: "#6D28D9", accent: "#EDE9FE" },
  { id: "magenta", name: "Magenta", primary: "#BE185D", accent: "#FCE7F3" },
  { id: "terracota", name: "Terracota", primary: "#C2410C", accent: "#FFEDD5" },
  { id: "verde", name: "Verde", primary: "#15803D", accent: "#DCFCE7" },
  { id: "ambar", name: "Ámbar", primary: "#B45309", accent: "#FEF3C7" },
  { id: "grafito", name: "Grafito", primary: "#334155", accent: "#E2E8F0" },
  { id: "indigo", name: "Índigo", primary: "#4338CA", accent: "#E0E7FF" },
  { id: "cian", name: "Cian", primary: "#0E7490", accent: "#CFFAFE" },
  { id: "esmeralda", name: "Esmeralda", primary: "#047857", accent: "#D1FAE5" },
  { id: "lima", name: "Lima", primary: "#4D7C0F", accent: "#ECFCCB" },
  { id: "rosa", name: "Rosa", primary: "#BE123C", accent: "#FFE4E6" },
  { id: "ciruela", name: "Ciruela", primary: "#86198F", accent: "#FAE8FF" },
  { id: "pizarra", name: "Pizarra", primary: "#475569", accent: "#F1F5F9" },
  { id: "cafe", name: "Café", primary: "#713F12", accent: "#FEF9C3" },
];

export const DEFAULT_PALETTE = PALETTES[0]!;

/**
 * La paleta elegida, por su identificador.
 *
 * Es la vía buena: la elección se guarda, no se adivina. `byColors` queda como
 * respaldo para las clínicas configuradas antes de que existiera el campo.
 */
export function paletteById(id: string | null | undefined): Palette | null {
  if (!id) return null;
  return PALETTES.find((palette) => palette.id === id) ?? null;
}

/**
 * La paleta que corresponde a un par de colores guardados.
 *
 * Devuelve null cuando la clínica tiene una combinación anterior a esta
 * lista. Ese caso no se pisa en silencio: cambiarle los colores a alguien
 * porque ya no están en el catálogo es rehacerle la marca sin avisar.
 */
export function findPalette(primary: string, accent: string): Palette | null {
  const p = primary.toLowerCase();
  const a = accent.toLowerCase();
  return (
    PALETTES.find(
      (palette) =>
        palette.primary.toLowerCase() === p &&
        palette.accent.toLowerCase() === a,
    ) ?? null
  );
}
